
// Maps ISO 3166-1 alpha-2 region codes to currency codes.
// Exported so edge middleware can map a geo-IP country header to a currency
// without pulling in the rest of this module's browser-only helpers.
export const REGION_CURRENCY: Record<string, string> = {
  AU: 'AUD', US: 'USD', GB: 'GBP', CA: 'CAD', NZ: 'NZD',
  SG: 'SGD', JP: 'JPY', CN: 'CNY', HK: 'HKD', IN: 'INR',
  BR: 'BRL', ZA: 'ZAR', MX: 'MXN', TR: 'TRY',
  PL: 'PLN', MY: 'MYR', TH: 'THB', ID: 'IDR', CZ: 'CZK',
  HU: 'HUF', PH: 'PHP', RO: 'RON', IL: 'ILS', AE: 'AED',
  CH: 'CHF', SE: 'SEK', NO: 'NOK', DK: 'DKK',
  DE: 'EUR', FR: 'EUR', IT: 'EUR', ES: 'EUR', NL: 'EUR',
  BE: 'EUR', AT: 'EUR', PT: 'EUR', FI: 'EUR', IE: 'EUR',
  GR: 'EUR', SK: 'EUR', SI: 'EUR', EE: 'EUR', LV: 'EUR',
  LT: 'EUR', LU: 'EUR', MT: 'EUR', CY: 'EUR',
};

// Fallback for locales with no region tag (e.g. "ja", "th").
const LANG_CURRENCY: Record<string, string> = {
  ja: 'JPY', zh: 'CNY', th: 'THB', id: 'IDR',
  ms: 'MYR', tr: 'TRY', pl: 'PLN',
  cs: 'CZK', hu: 'HUF', ro: 'RON', he: 'ILS', ar: 'AED',
};

// Maps an IANA timezone to the ISO 3166-1 alpha-2 country it sits in. Resolved
// through REGION_CURRENCY (above) so the currency for a country is defined in
// exactly one place: adding a country there and its zones here is all it takes.
//
// Coverage is the canonical zone for every supported country plus the
// deprecated / POSIX backward-compat aliases that older engines can still
// report (e.g. "US/Eastern", "Asia/Calcutta", "PRC", "Japan"). Any zone whose
// country is not in REGION_CURRENCY (or any unlisted zone such as "UTC" or
// "Etc/GMT+5") resolves to null and the caller falls through to the next signal.
//
// Intentionally client-only and NOT mirrored into
// functions/src/lib/currency-detect.ts: the server has no browser timezone to
// read, so it resolves currency from the request IP (MaxMind GeoLite2) and
// treats the client's detected value only as a hint.
const TZ_COUNTRY: Record<string, string> = {
  // United States (USD) — canonical zones, sub-zones, and legacy/POSIX aliases
  'America/New_York': 'US', 'America/Detroit': 'US', 'America/Chicago': 'US',
  'America/Denver': 'US', 'America/Boise': 'US', 'America/Phoenix': 'US',
  'America/Los_Angeles': 'US', 'America/Anchorage': 'US', 'America/Juneau': 'US',
  'America/Sitka': 'US', 'America/Metlakatla': 'US', 'America/Yakutat': 'US',
  'America/Nome': 'US', 'America/Adak': 'US', 'America/Menominee': 'US',
  'Pacific/Honolulu': 'US',
  'America/Kentucky/Louisville': 'US', 'America/Kentucky/Monticello': 'US',
  'America/Indiana/Indianapolis': 'US', 'America/Indiana/Vincennes': 'US',
  'America/Indiana/Winamac': 'US', 'America/Indiana/Marengo': 'US',
  'America/Indiana/Petersburg': 'US', 'America/Indiana/Vevay': 'US',
  'America/Indiana/Tell_City': 'US', 'America/Indiana/Knox': 'US',
  'America/North_Dakota/Center': 'US', 'America/North_Dakota/New_Salem': 'US',
  'America/North_Dakota/Beulah': 'US',
  'America/Atka': 'US', 'America/Indianapolis': 'US', 'America/Fort_Wayne': 'US',
  'America/Knox_IN': 'US', 'America/Louisville': 'US', 'America/Shiprock': 'US',
  'Navajo': 'US', 'US/Eastern': 'US', 'US/Central': 'US', 'US/Mountain': 'US',
  'US/Pacific': 'US', 'US/Arizona': 'US', 'US/Alaska': 'US', 'US/Aleutian': 'US',
  'US/Hawaii': 'US', 'US/Michigan': 'US', 'US/East-Indiana': 'US',
  'US/Indiana-Starke': 'US', 'EST5EDT': 'US', 'CST6CDT': 'US', 'MST7MDT': 'US',
  'PST8PDT': 'US', 'MST': 'US', 'HST': 'US',

  // Canada (CAD)
  'America/St_Johns': 'CA', 'America/Halifax': 'CA', 'America/Glace_Bay': 'CA',
  'America/Moncton': 'CA', 'America/Goose_Bay': 'CA', 'America/Toronto': 'CA',
  'America/Iqaluit': 'CA', 'America/Winnipeg': 'CA', 'America/Resolute': 'CA',
  'America/Rankin_Inlet': 'CA', 'America/Regina': 'CA', 'America/Swift_Current': 'CA',
  'America/Edmonton': 'CA', 'America/Cambridge_Bay': 'CA', 'America/Inuvik': 'CA',
  'America/Vancouver': 'CA', 'America/Dawson_Creek': 'CA', 'America/Fort_Nelson': 'CA',
  'America/Whitehorse': 'CA', 'America/Dawson': 'CA', 'America/Montreal': 'CA',
  'America/Nipigon': 'CA', 'America/Thunder_Bay': 'CA', 'America/Pangnirtung': 'CA',
  'America/Yellowknife': 'CA', 'America/Rainy_River': 'CA', 'America/Atikokan': 'CA',
  'America/Coral_Harbour': 'CA', 'America/Blanc-Sablon': 'CA', 'America/Creston': 'CA',
  'Canada/Atlantic': 'CA', 'Canada/Central': 'CA', 'Canada/Eastern': 'CA',
  'Canada/Mountain': 'CA', 'Canada/Newfoundland': 'CA', 'Canada/Pacific': 'CA',
  'Canada/Saskatchewan': 'CA', 'Canada/Yukon': 'CA',

  // Brazil (BRL)
  'America/Noronha': 'BR', 'America/Belem': 'BR', 'America/Fortaleza': 'BR',
  'America/Recife': 'BR', 'America/Araguaina': 'BR', 'America/Maceio': 'BR',
  'America/Bahia': 'BR', 'America/Sao_Paulo': 'BR', 'America/Campo_Grande': 'BR',
  'America/Cuiaba': 'BR', 'America/Santarem': 'BR', 'America/Porto_Velho': 'BR',
  'America/Boa_Vista': 'BR', 'America/Manaus': 'BR', 'America/Eirunepe': 'BR',
  'America/Rio_Branco': 'BR', 'America/Porto_Acre': 'BR', 'Brazil/Acre': 'BR',
  'Brazil/DeNoronha': 'BR', 'Brazil/East': 'BR', 'Brazil/West': 'BR',

  // Mexico (MXN)
  'America/Mexico_City': 'MX', 'America/Cancun': 'MX', 'America/Merida': 'MX',
  'America/Monterrey': 'MX', 'America/Matamoros': 'MX', 'America/Chihuahua': 'MX',
  'America/Ciudad_Juarez': 'MX', 'America/Ojinaga': 'MX', 'America/Mazatlan': 'MX',
  'America/Bahia_Banderas': 'MX', 'America/Hermosillo': 'MX', 'America/Tijuana': 'MX',
  'America/Ensenada': 'MX', 'America/Santa_Isabel': 'MX', 'Mexico/BajaNorte': 'MX',
  'Mexico/BajaSur': 'MX', 'Mexico/General': 'MX',

  // United Kingdom (GBP) — incl. Crown dependencies, which use GBP-par currency
  'Europe/London': 'GB', 'Europe/Belfast': 'GB', 'GB': 'GB', 'GB-Eire': 'GB',
  'Europe/Isle_of_Man': 'GB', 'Europe/Jersey': 'GB', 'Europe/Guernsey': 'GB',

  // Ireland (EUR)
  'Europe/Dublin': 'IE', 'Eire': 'IE',

  // Eurozone (EUR)
  'Europe/Paris': 'FR',
  'Europe/Berlin': 'DE', 'Europe/Busingen': 'DE',
  'Europe/Amsterdam': 'NL',
  'Europe/Brussels': 'BE',
  'Europe/Vienna': 'AT',
  'Europe/Luxembourg': 'LU',
  'Europe/Rome': 'IT',
  'Europe/Madrid': 'ES', 'Africa/Ceuta': 'ES', 'Atlantic/Canary': 'ES',
  'Europe/Lisbon': 'PT', 'Atlantic/Madeira': 'PT', 'Atlantic/Azores': 'PT', 'Portugal': 'PT',
  'Europe/Malta': 'MT',
  'Europe/Helsinki': 'FI', 'Europe/Mariehamn': 'FI',
  'Europe/Athens': 'GR',
  'Europe/Bratislava': 'SK',
  'Europe/Ljubljana': 'SI',
  'Europe/Tallinn': 'EE',
  'Europe/Riga': 'LV',
  'Europe/Vilnius': 'LT',
  'Asia/Nicosia': 'CY', 'Asia/Famagusta': 'CY', 'Europe/Nicosia': 'CY',

  // Other Europe
  'Europe/Zurich': 'CH',                                  // CHF
  'Europe/Stockholm': 'SE',                               // SEK
  'Europe/Oslo': 'NO',                                    // NOK
  'Europe/Copenhagen': 'DK',                              // DKK
  'Europe/Warsaw': 'PL',                                  // PLN
  'Europe/Prague': 'CZ',                                  // CZK
  'Europe/Budapest': 'HU',                                // HUF
  'Europe/Bucharest': 'RO',                               // RON
  'Europe/Istanbul': 'TR', 'Asia/Istanbul': 'TR',         // TRY

  // Australia (AUD)
  'Australia/Lord_Howe': 'AU', 'Australia/Sydney': 'AU', 'Australia/Melbourne': 'AU',
  'Australia/Brisbane': 'AU', 'Australia/Adelaide': 'AU', 'Australia/Darwin': 'AU',
  'Australia/Perth': 'AU', 'Australia/Hobart': 'AU', 'Australia/Eucla': 'AU',
  'Australia/Broken_Hill': 'AU', 'Australia/Lindeman': 'AU', 'Australia/Currie': 'AU',
  'Antarctica/Macquarie': 'AU', 'Australia/ACT': 'AU', 'Australia/Canberra': 'AU',
  'Australia/NSW': 'AU', 'Australia/North': 'AU', 'Australia/Queensland': 'AU',
  'Australia/South': 'AU', 'Australia/Tasmania': 'AU', 'Australia/Victoria': 'AU',
  'Australia/West': 'AU', 'Australia/Yancowinna': 'AU', 'Australia/LHI': 'AU',

  // New Zealand (NZD)
  'Pacific/Auckland': 'NZ', 'Pacific/Chatham': 'NZ', 'NZ': 'NZ', 'NZ-CHAT': 'NZ',

  // Asia / Middle East / Africa
  'Asia/Singapore': 'SG', 'Singapore': 'SG',              // SGD
  'Asia/Tokyo': 'JP', 'Japan': 'JP',                      // JPY
  'Asia/Shanghai': 'CN', 'Asia/Urumqi': 'CN', 'Asia/Chongqing': 'CN',
  'Asia/Chungking': 'CN', 'Asia/Harbin': 'CN', 'Asia/Kashgar': 'CN', 'PRC': 'CN', // CNY
  'Asia/Hong_Kong': 'HK', 'Hongkong': 'HK',               // HKD
  'Asia/Kolkata': 'IN', 'Asia/Calcutta': 'IN',            // INR
  'Asia/Kuala_Lumpur': 'MY', 'Asia/Kuching': 'MY',        // MYR
  'Asia/Bangkok': 'TH',                                   // THB
  'Asia/Jakarta': 'ID', 'Asia/Pontianak': 'ID', 'Asia/Makassar': 'ID',
  'Asia/Jayapura': 'ID', 'Asia/Ujung_Pandang': 'ID',      // IDR
  'Asia/Manila': 'PH',                                    // PHP
  'Asia/Jerusalem': 'IL', 'Asia/Tel_Aviv': 'IL', 'Israel': 'IL', // ILS
  'Asia/Dubai': 'AE',                                     // AED
  'Africa/Johannesburg': 'ZA',                            // ZAR
};

/** Cookie name written by edge middleware once it resolves the visitor's
 *  country from a geo-IP header. Read here so the client gets the more
 *  accurate IP-derived currency instead of the locale guess (which lies
 *  for travellers, English-OS-set-abroad users, etc.). */
export const GEO_CURRENCY_COOKIE = 'gc';

/** Returns the currency for an ISO 3166-1 alpha-2 country, or null if unmapped. */
export function currencyForCountry(country: string | null | undefined): string | null {
  if (!country) return null;
  return REGION_CURRENCY[country.toUpperCase()] ?? null;
}

function readGeoCurrencyCookie(): string | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(/(?:^|;\s*)gc=([A-Z]{3})(?:;|$)/);
  return match ? match[1] : null;
}

/** Currency implied by the device's IANA timezone, or null when the zone is
 *  unknown or carries no location signal (UTC, Etc/*). The OS timezone is
 *  auto-set from the device's physical location, so it is a far more reliable
 *  location proxy than navigator.language. Must only be called client-side:
 *  on the server Intl reports the host's timezone, not the visitor's. */
function currencyFromTimezone(): string | null {
  let zone: string | undefined;
  try {
    zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return null;
  }
  if (!zone) return null;
  return currencyForCountry(TZ_COUNTRY[zone] ?? null);
}

export function detectLocaleCurrency(): string {
  // Geo-IP cookie (set by middleware) wins. It reflects the visitor's actual
  // network location, not just whichever language their OS was configured in.
  const fromCookie = readGeoCurrencyCookie();
  if (fromCookie) return fromCookie;
  // Below here we need a browser. On the server (SSR) there is no navigator,
  // and Intl would report the host's timezone rather than the visitor's, so we
  // bail to USD and let the client recompute. Keeping this guard above the
  // timezone lookup is what stops SSR from stamping a bogus currency into the
  // markup based on where our server happens to run.
  if (typeof navigator === 'undefined') return 'USD';
  // Timezone is the next-best location signal after IP. The OS timezone is
  // almost always correct for the user's physical location, whereas
  // navigator.language is a manual preference that defaults to en-US for huge
  // numbers of non-US users. That mismatch is the main reason guests who never
  // set a browser currency kept seeing USD, so timezone is tried before locale.
  const fromTz = currencyFromTimezone();
  if (fromTz) return fromTz;
  const locale = navigator.languages?.[0] || navigator.language || 'en-US';
  const parts = locale.split('-');
  const region = parts[1]?.toUpperCase();
  const lang = parts[0]?.toLowerCase();
  if (region && REGION_CURRENCY[region]) return REGION_CURRENCY[region];
  if (lang && LANG_CURRENCY[lang]) return LANG_CURRENCY[lang];
  return 'USD';
}
